import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  Divider,
  Dialog,
  DialogContent,
} from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ListAltIcon from '@mui/icons-material/ListAlt';
import StraightenIcon from '@mui/icons-material/Straighten';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { keyframes } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';

const ACCENT = '#d32f2f';

const logoIn = keyframes`
  from { opacity: 0; transform: translate(6px, 12px) scale(0.96); }
  to { opacity: 1; transform: translateX(6px); }
`;

// SVG иконка Google (официальные цвета) — временно скрыто (ФЗ № 199-ФЗ)
// function GoogleIcon() {
//   return (
//     <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
//       <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
//       <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.859-3.048.859-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
//       <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
//       <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
//     </svg>
//   );
// }

export default function LoginScreen({ onContinueAsGuest }) {
  const { login, register, /* loginWithGoogle, */ resetPassword } = useAuth();

  // Режим: 'login' | 'register' | 'reset'
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [initials, setInitials] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  // const [googleLoading, setGoogleLoading] = useState(false);
  const [guestWarningOpen, setGuestWarningOpen] = useState(false);

  function clearForm() {
    setEmail('');
    setPassword('');
    setPasswordConfirm('');
    setInitials('');
    setError('');
    setMessage('');
  }

  function switchMode(newMode) {
    clearForm();
    setMode(newMode);
  }

  function getFirebaseErrorMessage(code) {
    switch (code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Неверный email или пароль';
      case 'auth/email-already-in-use':
        return 'Этот email уже зарегистрирован';
      case 'auth/weak-password':
        return 'Пароль должен содержать минимум 6 символов';
      case 'auth/invalid-email':
        return 'Неверный формат email';
      case 'auth/too-many-requests':
        return 'Слишком много попыток. Попробуйте позже';
      case 'auth/network-request-failed':
        return 'Ошибка сети. Проверьте подключение к интернету';
      default:
        return 'Произошла ошибка. Попробуйте снова';
    }
  }

  // async function handleGoogleLogin() {
  //   setError('');
  //   setGoogleLoading(true);
  //   try {
  //     await loginWithGoogle();
  //   } catch (err) {
  //     if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
  //       setError(getFirebaseErrorMessage(err.code));
  //     }
  //   }
  //   setGoogleLoading(false);
  // }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setMessage('');

    if (mode === 'register' && password !== passwordConfirm) {
      return setError('Пароли не совпадают');
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else if (mode === 'register') {
        if (!initials.trim()) {
          setLoading(false);
          return setError('Укажите инициалы');
        }
        await register(email, password, initials.trim());
      } else if (mode === 'reset') {
        await resetPassword(email);
        setMessage('Письмо для сброса пароля отправлено на ' + email);
        setLoading(false);
        return;
      }
    } catch (err) {
      setError(getFirebaseErrorMessage(err.code));
    }
    setLoading(false);
  }

  const titles = {
    login: 'Вход',
    register: 'Регистрация',
    reset: 'Сброс пароля',
  };

  const buttonLabels = {
    login: 'Войти',
    register: 'Зарегистрироваться',
    reset: 'Отправить письмо',
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 50%, #fafafa 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Декоративные элементы фона */}
      <Box sx={{
        position: 'absolute', top: '-100px', right: '-100px',
        width: '400px', height: '400px', borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(211,47,47,0.06) 0%, rgba(211,47,47,0.02) 100%)',
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-150px', left: '-150px',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(211,47,47,0.04) 0%, rgba(211,47,47,0.01) 100%)',
        pointerEvents: 'none',
      }} />

      <Card
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 420,
          mx: 2,
          borderRadius: 3,
          border: '1px solid #f0f0f0',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Логотип / заголовок */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/proxytherm-logo.png"
              alt="ProxyTherm"
              sx={{
                height: 46,
                width: 'auto',
                mb: 1.25,
                display: 'block',
                mx: 'auto',
                transform: 'translateX(6px)',
                animation: `${logoIn} 0.7s ease`,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
              ProxythermCAD
            </Typography>
            <Typography variant="body2" sx={{ color: '#888' }}>
              {titles[mode]}
            </Typography>
          </Box>

          {/* Форма */}
          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            {message && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                {message}
              </Alert>
            )}

            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              autoComplete="email"
            />

            {mode === 'register' && (
              <TextField
                label="Инициалы (например: Иванов И.И.)"
                type="text"
                value={initials}
                onChange={(e) => setInitials(e.target.value)}
                required
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                autoComplete="off"
              />
            )}

            {mode !== 'reset' && (
              <TextField
                label="Пароль"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
            )}

            {mode === 'register' && (
              <TextField
                label="Подтвердите пароль"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                autoComplete="new-password"
              />
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mt: 1,
                mb: 2,
                py: 1.2,
                borderRadius: 2,
                backgroundColor: ACCENT,
                fontWeight: 600,
                fontSize: '0.95rem',
                '&:hover': { backgroundColor: '#b71c1c' },
                '&:disabled': { backgroundColor: '#e0e0e0' },
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : buttonLabels[mode]}
            </Button>

            {/* Кнопка Google — временно скрыто (ФЗ № 199-ФЗ, с 07.07.2026)
            {mode !== 'reset' && (
              <>
                <Divider sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#bdbdbd', px: 1 }}>или</Typography>
                </Divider>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading || loading}
                  startIcon={googleLoading ? <CircularProgress size={16} /> : <GoogleIcon />}
                  sx={{
                    mb: 2,
                    py: 1.1,
                    borderRadius: 2,
                    borderColor: '#e0e0e0',
                    color: '#444',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    background: '#fff',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#bdbdbd', background: '#fafafa' },
                  }}
                >
                  {googleLoading ? 'Выполняется вход...' : 'Войти через Google'}
                </Button>
              </>
            )}
            */}

            <Divider sx={{ mb: 2 }} />

            {/* Ссылки переключения режима */}
            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {mode === 'login' && (
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Нет аккаунта?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => switchMode('register')}
                    sx={{ color: ACCENT, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Зарегистрироваться
                  </Link>
                </Typography>
              )}
              {mode === 'register' && (
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Уже есть аккаунт?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => switchMode('login')}
                    sx={{ color: ACCENT, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Войти
                  </Link>
                </Typography>
              )}
              {mode === 'reset' && (
                <Link
                  component="button"
                  type="button"
                  onClick={() => switchMode('login')}
                  sx={{ color: ACCENT, fontWeight: 600, cursor: 'pointer' }}
                >
                  ← Вернуться к входу
                </Link>
              )}
            </Box>
          </Box>
        </CardContent>

        {/* Кнопка "без авторизации" — только на экране входа */}
        {mode === 'login' && onContinueAsGuest && (
          <Box sx={{ textAlign: 'center', mt: 2.5, mb: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setGuestWarningOpen(true)}
              sx={{
                fontSize: '0.82rem',
                color: '#d32f2f',
                borderColor: 'rgba(211,47,47,0.3)',
                borderRadius: '0.6rem',
                textTransform: 'none',
                fontWeight: 600,
                px: 3, py: 0.9,
                background: '#fff',
                '&:hover': { borderColor: '#d32f2f', background: 'rgba(211,47,47,0.04)', color: '#b71c1c' },
              }}
            >
              Продолжить без авторизации
            </Button>
          </Box>
        )}
      </Card>

      {/* Диалог-предупреждение о режиме гостя */}
      <Dialog
        open={guestWarningOpen}
        onClose={() => setGuestWarningOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 2rem 5rem rgba(0,0,0,0.18)' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Шапка */}
          <Box sx={{ background: 'linear-gradient(135deg, #424242, #616161)', px: 3, pt: 3, pb: 2.5, textAlign: 'center' }}>
            <Box sx={{ width: '3.5rem', height: '3.5rem', borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
              <CloudOffIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#fff', mb: 0.5 }}>
              Без аккаунта вы теряете многое
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
              В режиме гостя ряд важных функций будет недоступен
            </Typography>
          </Box>

          {/* Список ограничений */}
          <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {[
              { icon: <SaveIcon sx={{ fontSize: '1.1rem', color: '#d32f2f' }} />, title: 'Нельзя сохранить чертёж .ptCAD', desc: 'Единственный формат, с которым можно продолжить работу позже. Без него всё что вы сделаете — будет потеряно при закрытии.' },
              { icon: <HistoryIcon sx={{ fontSize: '1.1rem', color: '#1565c0' }} />, title: 'Нет истории чертежей', desc: 'Все ваши работы хранятся в облаке и доступны с любого устройства — только при наличии аккаунта.' },
              { icon: <PictureAsPdfIcon sx={{ fontSize: '1.1rem', color: '#e65100' }} />, title: 'Нельзя сохранить PDF', desc: 'Экспорт чертежа в PDF для печати и передачи заказчику доступен только авторизованным пользователям.' },
              { icon: <ListAltIcon sx={{ fontSize: '1.1rem', color: '#2e7d32' }} />, title: 'Нельзя сохранить спецификацию', desc: 'Автоматически сформированный список материалов и оборудования — только для зарегистрированных.' },
              { icon: <StraightenIcon sx={{ fontSize: '1.1rem', color: '#6a1b9a' }} />, title: 'Нельзя сохранить DWG и STEP', desc: 'Чертёж AutoCAD и 3D-модель сборки доступны только зарегистрированным пользователям.' },
              { icon: <ViewInArIcon sx={{ fontSize: '1.1rem', color: '#0277bd' }} />, title: 'Нет просмотра 3D и видов', desc: 'Просмотр модели и добавление видов сверху, снизу, слева и справа — только с аккаунтом.' },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', background: '#fafafa', border: '0.0625rem solid #f0f0f0', borderRadius: '0.6rem', px: 1.75, py: 1.25 }}>
                <Box sx={{ flexShrink: 0, mt: 0.15 }}>{item.icon}</Box>
                <Box>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#212121', mb: 0.2 }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#757575', lineHeight: 1.45 }}>{item.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Кнопки */}
          <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { setGuestWarningOpen(false); switchMode('register'); }}
              sx={{ py: 1.2, borderRadius: '0.6rem', background: 'linear-gradient(135deg, #d32f2f, #e53935)', fontWeight: 700, fontSize: '0.9rem', textTransform: 'none', boxShadow: '0 0.25rem 1rem rgba(211,47,47,0.35)', '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #d32f2f)' } }}
            >
              Зарегистрироваться — это бесплатно
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => { setGuestWarningOpen(false); onContinueAsGuest(); }}
              sx={{ py: 0.9, borderRadius: '0.6rem', fontSize: '0.78rem', color: '#9e9e9e', textTransform: 'none', '&:hover': { background: '#f5f5f5', color: '#616161' } }}
            >
              Всё равно продолжить без аккаунта
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
